import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Autocomplete from "@mui/material/Autocomplete";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ClearIcon from "@mui/icons-material/Clear";
import PaymentIcon from "@mui/icons-material/Payment";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ShareIcon from "@mui/icons-material/Share";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import Container from "../../GeneralComponents/Container";
import { Topbar, Sidebar, Table } from "../components";
import {
  getReferrals,
  markReferralPayment,
  getOrganizations,
  getOrganizationById,
} from "../../../redux/actions/apiActions";
import setGlobalLoader from "../../../redux/actions/loaderActions";
import { PROFILES, REFERRAL_STATUS, REFERRAL_PAYMENT_STATUS } from "../../../utils/constants";

const isProd = process.env.REACT_APP_ENV === "production";
const BASE_DOMAIN = isProd ? "drivolo.com" : "uat.drivolo.com";

const HOW_IT_WORKS_STEPS = [
  {
    Icon: ShareIcon,
    color: "#1f51d8",
    bg: "#e8f0fe",
    title: "Share your link",
    desc: "Send your unique referral link to another driving school owner.",
  },
  {
    Icon: PersonAddIcon,
    color: "#2e7d32",
    bg: "#e8f5e9",
    title: "They sign up",
    desc: "They register on Drivolo using your link and add at least 5 students.",
  },
  {
    Icon: CurrencyRupeeIcon,
    color: "#ed6c02",
    bg: "#fff3e0",
    title: "You both earn ₹250",
    desc: "Once their school is activated, payment is processed within 7 days for both parties.",
  },
];

const OrgHeroCard = () => (
  <Card
    sx={{
      mb: 3,
      background: "linear-gradient(135deg, #1f51d8 0%, #1a44c2 100%)",
      color: "#fff",
      border: "none",
    }}
    elevation={0}
  >
    <CardContent sx={{ textAlign: "center", py: 4 }}>
      <Box
        sx={{
          display: "inline-flex",
          bgcolor: "rgba(255,255,255,0.2)",
          borderRadius: "50%",
          p: 1.5,
          mb: 2,
        }}
      >
        <CardGiftcardIcon sx={{ fontSize: 40 }} />
      </Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Refer &amp; Earn
      </Typography>
      <Typography variant="body1" sx={{ opacity: 0.9 }}>
        Invite a driving school to Drivolo and{" "}
        <Box component="span" fontWeight={700}>
          both of you earn ₹250!
        </Box>
      </Typography>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          mt: 1.5,
          bgcolor: "rgba(255,255,255,0.15)",
          borderRadius: 4,
          px: 1.5,
          py: 0.5,
        }}
      >
        <Typography variant="caption">{BASE_DOMAIN}</Typography>
      </Box>
    </CardContent>
  </Card>
);

const OrgReferralLinkCard = ({ referralLink }) => {
  const handleCopy = () => {
    navigator.clipboard
      .writeText(referralLink)
      .then(() => toast.success("Referral link copied!"))
      .catch(() => toast.error("Could not copy link"));
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join Drivolo — Refer & Earn ₹250",
          text: "Join Drivolo and grow your driving school!\n\nUse my referral link to sign up and we both earn ₹250:",
          url: referralLink,
        });
      } catch {
        // user cancelled share
      }
    } else {
      handleCopy();
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={700}
          letterSpacing={1}
        >
          YOUR REFERRAL LINK
        </Typography>
        <Box
          sx={{
            mt: 1.5,
            p: 1.5,
            bgcolor: "action.hover",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              wordBreak: "break-all",
              fontFamily: "monospace",
              fontSize: 13,
            }}
          >
            {referralLink}
          </Typography>
          <Tooltip title="Copy link">
            <IconButton size="small" onClick={handleCopy}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Button
          variant="contained"
          startIcon={<ShareIcon />}
          onClick={handleShare}
          fullWidth
          sx={{ mt: 2 }}
        >
          Share Link
        </Button>
      </CardContent>
    </Card>
  );
};

const HowItWorksCard = () => (
  <Card variant="outlined" sx={{ mb: 3 }}>
    <CardContent>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={700}
        letterSpacing={1}
      >
        HOW IT WORKS
      </Typography>
      <Box mt={2} display="flex" flexDirection="column" gap={2}>
        {HOW_IT_WORKS_STEPS.map((step, i) => (
          <Box key={i} display="flex" gap={2} alignItems="flex-start">
            <Box
              sx={{
                flexShrink: 0,
                width: 44,
                height: 44,
                borderRadius: "50%",
                bgcolor: step.bg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <step.Icon sx={{ color: step.color, fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="body2" fontWeight={700}>
                {step.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {step.desc}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </CardContent>
  </Card>
);

const STATUS_LABELS = {
  [REFERRAL_STATUS.PENDING]: "Pending",
  [REFERRAL_STATUS.ELIGIBLE]: "Eligible",
  [REFERRAL_STATUS.AWAITING_PAYMENT]: "Awaiting Payment",
  [REFERRAL_STATUS.PAID_BOTH]: "Paid",
  [REFERRAL_STATUS.EXPIRED]: "Expired",
};

const STATUS_COLORS = {
  [REFERRAL_STATUS.PENDING]: "default",
  [REFERRAL_STATUS.ELIGIBLE]: "info",
  [REFERRAL_STATUS.AWAITING_PAYMENT]: "warning",
  [REFERRAL_STATUS.PAID_BOTH]: "success",
  [REFERRAL_STATUS.EXPIRED]: "error",
};

const TableComponent = React.forwardRef(({ columns, data, title, isRemoteData }, ref) => {
  const theme = useTheme();
  return (
    <Box p={4}>
      <Box
        width={1}
        height={1}
        borderRadius={2}
        border={`2px solid ${theme.palette.divider}`}
        sx={{ borderStyle: "dashed" }}
      >
        <Table
          ref={ref}
          columns={columns}
          data={data}
          export={false}
          selection={false}
          toolbar
          title={title}
          isRemoteData={isRemoteData}
        />
      </Box>
    </Box>
  );
});

// ── Mark Payment Dialog ──────────────────────────────────────────────────────
const MarkPaymentDialog = ({ open, referral, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const [party, setParty] = useState("referrer");
  const [transactionRef, setTransactionRef] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      await markReferralPayment(referral._id, { party, transactionRef: transactionRef || null });
      toast.success(`Payment for ${party} marked as paid.`);
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Error marking payment.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!referral) return null;

  const referrerPaid = referral.referrerPayment?.status === REFERRAL_PAYMENT_STATUS.PAID;
  const referredPaid = referral.referredPayment?.status === REFERRAL_PAYMENT_STATUS.PAID;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Mark Payment</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <TextField
            select
            label="Party"
            value={party}
            onChange={(e) => setParty(e.target.value)}
            size="small"
            fullWidth
          >
            <MenuItem value="referrer" disabled={referrerPaid}>
              Referrer {referrerPaid ? "(already paid)" : ""}
            </MenuItem>
            <MenuItem value="referred" disabled={referredPaid}>
              Referred {referredPaid ? "(already paid)" : ""}
            </MenuItem>
          </TextField>
          <TextField
            label="Transaction Reference (optional)"
            value={transactionRef}
            onChange={(e) => setTransactionRef(e.target.value)}
            size="small"
            fullWidth
            placeholder="UTR / UPI Ref / Bank Ref"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || (referrerPaid && referredPaid)}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Org: "How you were referred" card ───────────────────────────────────────
const AsReferredCard = ({ referral }) => {
  const theme = useTheme();
  if (!referral) {
    return (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            How You Were Referred
          </Typography>
          <Typography color="text.secondary">
            You joined without a referral code.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          How You Were Referred
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={3} mt={1}>
          <Box>
            <Typography variant="caption" color="text.secondary">Referred By</Typography>
            <Typography fontWeight={600}>{referral.referrerOrgId?.name ?? "-"}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Status</Typography>
            <Box mt={0.5}>
              <Chip
                size="small"
                label={STATUS_LABELS[referral.status] ?? referral.status}
                color={STATUS_COLORS[referral.status] ?? "default"}
              />
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Payout Amount</Typography>
            <Typography fontWeight={600}>
              {referral.payoutAmount != null ? `₹${referral.payoutAmount}` : "-"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Your Payment</Typography>
            <Box mt={0.5}>
              <Chip
                size="small"
                label={referral.referredPayment?.status === REFERRAL_PAYMENT_STATUS.PAID ? "Paid" : "Pending"}
                color={referral.referredPayment?.status === REFERRAL_PAYMENT_STATUS.PAID ? "success" : "default"}
              />
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Joined On</Typography>
            <Typography>{referral.createdAt ? format(new Date(referral.createdAt), "MMM dd, yyyy") : "-"}</Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// ── Main Component ───────────────────────────────────────────────────────────
const Referrals = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const profile = useSelector((state) => state.authReducer.profile);
  const user = useSelector((state) => state.authReducer.user);
  const referralLink = `https://${BASE_DOMAIN}/signup?ref=${user?._id}`;
  const tableRef = useRef(null);
  const isMd = useMediaQuery(theme.breakpoints.up("md"), { defaultMatches: true });

  const [openSidebar, setOpenSidebar] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState({ open: false, referral: null });

  // ── org-specific state ──
  const [asReferred, setAsReferred] = useState(undefined); // undefined = not loaded yet
  const [orgReferralsLoaded, setOrgReferralsLoaded] = useState(false);
  const [bankDetailsMissing, setBankDetailsMissing] = useState(false);

  useEffect(() => {
    if (profile === PROFILES.ORGANIZATION && user?._id) {
      getOrganizationById(user._id)
        .then((res) => {
          const org = res?.data?.data;
          const missing = !org?.bankAccountHolderName || !org?.bankAccountNumber || !org?.bankIFSCCode;
          setBankDetailsMissing(missing);
        })
        .catch(() => {});
    }
  }, [profile, user?._id]);

  // ── admin filter state ──
  const today = new Date();
  const minDateObj = new Date("2025-01-01");

  const getDefaultStartDate = () =>
    profile === PROFILES.ADMIN ? new Date(today.getFullYear(), today.getMonth(), 1) : null;
  const getDefaultEndDate = () => (profile === PROFILES.ADMIN ? today : null);

  const [dateRange, setDateRange] = useState([
    { startDate: getDefaultStartDate(), endDate: getDefaultEndDate(), key: "selection" },
  ]);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const startDate = dateRange[0].startDate;
  const endDate = dateRange[0].endDate;

  const [statusFilter, setStatusFilter] = useState("");
  const [organizations, setOrganizations] = useState([]);
  const [selectedReferrerOrg, setSelectedReferrerOrg] = useState(null);
  const [selectedReferredOrg, setSelectedReferredOrg] = useState(null);

  // fetch org list for admin dropdowns
  useEffect(() => {
    if (profile === PROFILES.ADMIN) {
      getOrganizations("?limit=10000")
        .then((res) => setOrganizations(res?.data?.data?.docs || []))
        .catch(() => {});
    }
  }, [profile]);

  // ── Fetch referrals ──────────────────────────────────────────────────────
  const fetchReferrals = async (query) => {
    return new Promise(async (resolve) => {
      try {
        dispatch(setGlobalLoader(true));
        const page = query.page + 1;
        const limit = query.pageSize;
        const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });

        if (startDate) params.append("startDate", format(startDate, "yyyy-MM-dd"));
        if (endDate) params.append("endDate", format(endDate, "yyyy-MM-dd"));
        if (statusFilter) params.append("status", statusFilter);
        if (selectedReferrerOrg) params.append("referrerOrgId", selectedReferrerOrg._id);
        if (selectedReferredOrg) params.append("referredOrgId", selectedReferredOrg._id);

        const res = await getReferrals(`?${params.toString()}`);
        const apiData = res?.data?.data;
        dispatch(setGlobalLoader(false));

        // Admin gets flat paginated list
        const docs = apiData?.docs ?? [];
        const data = docs.map((d, i) => ({
          ...d,
          index: (page - 1) * limit + i + 1,
          referrerName: d.referrerOrgId?.name ?? "-",
          referredName: d.referredOrgId?.name ?? "-",
          payoutAmountDisplay: d.payoutAmount != null ? `₹${d.payoutAmount}` : "-",
          eligibleAtDisplay: d.eligibleAt ? format(new Date(d.eligibleAt), "MMM dd, yyyy") : "-",
          payoutDueAtDisplay: d.payoutDueAt ? format(new Date(d.payoutDueAt), "MMM dd, yyyy") : "-",
          createdAtDisplay: format(new Date(d.createdAt), "MMM dd, yyyy"),
        }));

        resolve({ data, page: query.page, totalCount: apiData?.total || 0 });
      } catch (err) {
        dispatch(setGlobalLoader(false));
        toast.error(err?.response?.data?.message || "Error fetching referrals");
        resolve({ data: [], page: 0, totalCount: 0 });
      }
    });
  };

  // Org view: fetch all data (asReferrer paginated + asReferred) once on mount
  const fetchOrgReferrals = async (query) => {
    return new Promise(async (resolve) => {
      try {
        dispatch(setGlobalLoader(true));
        const page = query.page + 1;
        const limit = query.pageSize;
        const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });

        const res = await getReferrals(`?${params.toString()}`);
        const apiData = res?.data?.data;
        dispatch(setGlobalLoader(false));

        // Persist asReferred once
        if (!orgReferralsLoaded) {
          setAsReferred(apiData?.asReferred ?? null);
          setOrgReferralsLoaded(true);
        }

        const docs = apiData?.asReferrer?.docs ?? [];
        const data = docs.map((d, i) => ({
          ...d,
          index: (page - 1) * limit + i + 1,
          referredName: d.referredOrgId?.name ?? "-",
          payoutAmountDisplay: d.payoutAmount != null ? `₹${d.payoutAmount}` : "-",
          eligibleAtDisplay: d.eligibleAt ? format(new Date(d.eligibleAt), "MMM dd, yyyy") : "-",
          payoutDueAtDisplay: d.payoutDueAt ? format(new Date(d.payoutDueAt), "MMM dd, yyyy") : "-",
          createdAtDisplay: format(new Date(d.createdAt), "MMM dd, yyyy"),
        }));

        resolve({ data, page: query.page, totalCount: apiData?.asReferrer?.total || 0 });
      } catch (err) {
        dispatch(setGlobalLoader(false));
        toast.error(err?.response?.data?.message || "Error fetching referrals");
        resolve({ data: [], page: 0, totalCount: 0 });
      }
    });
  };

  // ── Columns ──────────────────────────────────────────────────────────────
  const adminColumns = [
    { title: "Sr no.", field: "index", filtering: false },
    { title: "ID", field: "displayId", filtering: false },
    { title: "Referrer", field: "referrerName", filtering: false },
    { title: "Referred", field: "referredName", filtering: false },
    {
      title: "Status",
      field: "status",
      filtering: false,
      render: (row) => (
        <Chip
          size="small"
          label={STATUS_LABELS[row.status] ?? row.status}
          color={STATUS_COLORS[row.status] ?? "default"}
        />
      ),
    },
    { title: "Payout", field: "payoutAmountDisplay", filtering: false },
    { title: "Eligible On", field: "eligibleAtDisplay", filtering: false },
    { title: "Due By", field: "payoutDueAtDisplay", filtering: false },
    {
      title: "Referrer Pmt",
      field: "referrerPayment",
      filtering: false,
      render: (row) => (
        <Chip
          size="small"
          label={row.referrerPayment?.status === REFERRAL_PAYMENT_STATUS.PAID ? "Paid" : "Pending"}
          color={row.referrerPayment?.status === REFERRAL_PAYMENT_STATUS.PAID ? "success" : "default"}
        />
      ),
    },
    {
      title: "Referred Pmt",
      field: "referredPayment",
      filtering: false,
      render: (row) => (
        <Chip
          size="small"
          label={row.referredPayment?.status === REFERRAL_PAYMENT_STATUS.PAID ? "Paid" : "Pending"}
          color={row.referredPayment?.status === REFERRAL_PAYMENT_STATUS.PAID ? "success" : "default"}
        />
      ),
    },
    { title: "Created", field: "createdAtDisplay", filtering: false },
    {
      title: "Actions",
      field: "",
      filtering: false,
      render: (row) => (
        <Box display="flex" gap={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={() => navigate(`/referrals/view/${row._id}`)}
          >
            View
          </Button>
          {row.status === REFERRAL_STATUS.AWAITING_PAYMENT && (
            <Button
              size="small"
              variant="contained"
              startIcon={<PaymentIcon />}
              onClick={() => setPaymentDialog({ open: true, referral: row })}
            >
              Pay
            </Button>
          )}
        </Box>
      ),
    },
  ];

  const orgColumns = [
    { title: "Sr no.", field: "index", filtering: false },
    { title: "ID", field: "displayId", filtering: false },
    { title: "Referred Org", field: "referredName", filtering: false },
    {
      title: "Status",
      field: "status",
      filtering: false,
      render: (row) => (
        <Chip
          size="small"
          label={STATUS_LABELS[row.status] ?? row.status}
          color={STATUS_COLORS[row.status] ?? "default"}
        />
      ),
    },
    { title: "Payout", field: "payoutAmountDisplay", filtering: false },
    { title: "Eligible On", field: "eligibleAtDisplay", filtering: false },
    { title: "Due By", field: "payoutDueAtDisplay", filtering: false },
    {
      title: "Your Payment",
      field: "referrerPayment",
      filtering: false,
      render: (row) => (
        <Chip
          size="small"
          label={row.referrerPayment?.status === REFERRAL_PAYMENT_STATUS.PAID ? "Paid" : "Pending"}
          color={row.referrerPayment?.status === REFERRAL_PAYMENT_STATUS.PAID ? "success" : "default"}
        />
      ),
    },
    { title: "Referred On", field: "createdAtDisplay", filtering: false },
    {
      title: "Actions",
      field: "",
      filtering: false,
      render: (row) => (
        <Button
          size="small"
          variant="outlined"
          startIcon={<VisibilityIcon />}
          onClick={() => navigate(`/referrals/view/${row._id}`)}
        >
          View
        </Button>
      ),
    },
  ];

  const open = isMd ? false : openSidebar;

  return (
    <Box>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
        elevation={0}
      >
        <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
          <Topbar onSidebarOpen={() => setOpenSidebar(true)} />
        </Container>
      </AppBar>
      <Sidebar
        onClose={() => setOpenSidebar(false)}
        open={open}
        variant={isMd ? "permanent" : "temporary"}
      />
      <main>
        <Box height={{ xs: 94, sm: 102, md: 107 }} />
        <Box display="flex" flex="1 1 auto" overflow="hidden" paddingLeft={{ md: "256px" }}>
          <Box display="flex" flex="1 1 auto" overflow="hidden">
            <Box flex="1 1 auto" height="100%" overflow="auto">

              {/* ── Admin View ────────────────────────────────────────────── */}
              {profile === PROFILES.ADMIN && (
                <>
                  <Box
                    marginTop={3}
                    mx={{ xs: 3, md: 4 }}
                    display="flex"
                    flexDirection="column"
                    gap={2}
                  >
                    {/* Filter row */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                      <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                        {/* Date range */}
                        <Box sx={{ position: "relative" }}>
                          <TextField
                            size="small"
                            value={
                              startDate && endDate
                                ? `${format(startDate, "MMM dd, yyyy")} - ${format(endDate, "MMM dd, yyyy")}`
                                : ""
                            }
                            placeholder="Select date range"
                            onClick={() => setShowDatePicker(!showDatePicker)}
                            InputProps={{
                              readOnly: true,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <CalendarMonthIcon />
                                </InputAdornment>
                              ),
                              endAdornment: startDate && endDate && (
                                <InputAdornment position="end">
                                  <ClearIcon
                                    sx={{ cursor: "pointer" }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setDateRange([{ startDate: null, endDate: null, key: "selection" }]);
                                      setShowDatePicker(false);
                                    }}
                                  />
                                </InputAdornment>
                              ),
                            }}
                            sx={{ minWidth: 280, cursor: "pointer" }}
                          />
                          {showDatePicker && (
                            <Box
                              sx={{
                                position: "absolute",
                                top: "100%",
                                left: 0,
                                zIndex: 1000,
                                mt: 1,
                                boxShadow: 3,
                                borderRadius: 1,
                                bgcolor: "background.paper",
                              }}
                            >
                              <DateRangePicker
                                onChange={(item) => setDateRange([item.selection])}
                                ranges={[{
                                  startDate: dateRange[0].startDate || new Date(),
                                  endDate: dateRange[0].endDate || new Date(),
                                  key: "selection",
                                }]}
                                minDate={minDateObj}
                                maxDate={today}
                                rangeColors={[theme.palette.primary.main]}
                                locale={enUS}
                              />
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  p: 1,
                                  borderTop: `1px solid ${theme.palette.divider}`,
                                }}
                              >
                                <Button size="small" onClick={() => setShowDatePicker(false)}>
                                  Close
                                </Button>
                              </Box>
                            </Box>
                          )}
                        </Box>

                        {/* Status filter */}
                        <TextField
                          select
                          size="small"
                          label="Status"
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          sx={{ minWidth: 170 }}
                        >
                          <MenuItem value="">All</MenuItem>
                          {Object.entries(STATUS_LABELS).map(([val, label]) => (
                            <MenuItem key={val} value={val}>{label}</MenuItem>
                          ))}
                        </TextField>

                        {/* Referrer org */}
                        <Autocomplete
                          size="small"
                          options={organizations}
                          getOptionLabel={(o) => (typeof o === "string" ? o : o?.name || "")}
                          value={selectedReferrerOrg}
                          onChange={(_, v) => setSelectedReferrerOrg(v)}
                          renderOption={(props, option) => (
                            <li {...props} key={option._id}>{option.name}</li>
                          )}
                          renderInput={(params) => (
                            <TextField {...params} label="Referrer Org" placeholder="Search..." />
                          )}
                          isOptionEqualToValue={(o, v) => o?._id === v?._id}
                          sx={{ minWidth: 220 }}
                        />

                        {/* Referred org */}
                        <Autocomplete
                          size="small"
                          options={organizations}
                          getOptionLabel={(o) => (typeof o === "string" ? o : o?.name || "")}
                          value={selectedReferredOrg}
                          onChange={(_, v) => setSelectedReferredOrg(v)}
                          renderOption={(props, option) => (
                            <li {...props} key={option._id}>{option.name}</li>
                          )}
                          renderInput={(params) => (
                            <TextField {...params} label="Referred Org" placeholder="Search..." />
                          )}
                          isOptionEqualToValue={(o, v) => o?._id === v?._id}
                          sx={{ minWidth: 220 }}
                        />

                        <Button
                          variant="outlined"
                          size="medium"
                          onClick={() => tableRef.current?.onQueryChange()}
                        >
                          Filter
                        </Button>
                        <Button
                          variant="text"
                          color="error"
                          size="medium"
                          onClick={() => {
                            setDateRange([{
                              startDate: getDefaultStartDate(),
                              endDate: getDefaultEndDate(),
                              key: "selection",
                            }]);
                            setStatusFilter("");
                            setSelectedReferrerOrg(null);
                            setSelectedReferredOrg(null);
                            setShowDatePicker(false);
                            setTimeout(() => tableRef.current?.onQueryChange(), 100);
                          }}
                        >
                          Reset
                        </Button>
                      </Box>
                    </Box>
                  </Box>

                  <TableComponent
                    ref={tableRef}
                    data={fetchReferrals}
                    columns={adminColumns}
                    title="Referrals"
                    isRemoteData={true}
                  />
                </>
              )}

              {/* ── Organization View ────────────────────────────────────── */}
              {profile === PROFILES.ORGANIZATION && (
                <Box mx={{ xs: 3, md: 4 }} mt={3}>
                  {/* Hero + referral link + how it works */}
                  <OrgHeroCard />
                  {bankDetailsMissing && (
                    <Alert
                      severity="warning"
                      sx={{ mb: 3 }}
                      action={
                        <Button
                          color="inherit"
                          size="small"
                          onClick={() => navigate("/profile")}
                        >
                          Update Now
                        </Button>
                      }
                    >
                      <AlertTitle>Bank Details Required</AlertTitle>
                      To receive your referral payment, please update your bank details in your Profile.
                    </Alert>
                  )}
                  <OrgReferralLinkCard referralLink={referralLink} />
                  <HowItWorksCard />

                  {/* How this org was referred */}
                  <AsReferredCard referral={asReferred} />

                  {/* Orgs this org has referred */}
                  <Typography variant="h6" fontWeight={700} mb={2}>
                    Organizations You Referred
                  </Typography>
                  <TableComponent
                    ref={tableRef}
                    data={fetchOrgReferrals}
                    columns={orgColumns}
                    title="Referrals Sent"
                    isRemoteData={true}
                  />

                  {/* Terms link */}
                  <Box display="flex" justifyContent="center" mt={2} mb={3}>
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<ArticleOutlinedIcon />}
                      endIcon={<OpenInNewIcon fontSize="inherit" />}
                      onClick={() => navigate("/referral-terms-and-conditions")}
                    >
                      View Terms &amp; Conditions
                    </Button>
                  </Box>
                </Box>
              )}

              <Divider />
            </Box>
          </Box>
        </Box>
      </main>

      <MarkPaymentDialog
        open={paymentDialog.open}
        referral={paymentDialog.referral}
        onClose={() => setPaymentDialog({ open: false, referral: null })}
        onSuccess={() => tableRef.current?.onQueryChange()}
      />
    </Box>
  );
};

export default Referrals;
